// src/components/shell/popovers/ActivityBannerAdAlertsSection.tsx
// Rolls the legacy `x/x ads` header pill into the activity banner so the
// banner is the single home for notifications + impersonation + ad alerts +
// achievements (per doctrine §4 / D2).
//
// Single shell-level fetch: `LeadWorkspaceShell` calls `useAdsHeaderActivity`
// once and publishes the result via `WorkspaceUrgencyContext`. This banner
// section reads `adsActivity` from that context — there is no longer a
// per-banner polling interval. The `open` prop is still accepted so future
// callers can use it for visibility-only concerns (e.g. animation gating),
// but it no longer gates the fetch.
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { StatusDot } from "@/components/common/kpi";
import { resolveAdsHeaderActivityPill } from "@/components/ads-assistant/resolveAdsHeaderActivityPill";
import { canAccessAdsAssistant } from "@/lib/adsAssistantPermissions";

export type ActivityBannerAdAlertsSectionProps = {
  /**
   * Whether the parent popover is open. Retained for future visibility-only
   * concerns but no longer gates the fetch (the shell polls once and feeds
   * every consumer via context).
   */
  open?: boolean;
  /**
   * Called when the user clicks the ad-alerts row. The parent (LeadWorkspaceShell)
   * is expected to set the Ads ingestion intent + switch the mode to
   * adsAssistant. The popover will close on its own once the underlying mode
   * change re-renders the chrome.
   */
  onOpenAdsIngestion?: () => void;
};

export function ActivityBannerAdAlertsSection({
  onOpenAdsIngestion,
}: ActivityBannerAdAlertsSectionProps) {
  const auth = useAuth();
  const urgency = useWorkspaceUrgencyContext();
  const activity = urgency?.adsActivity ?? null;

  if (!canAccessAdsAssistant(auth)) return null;

  const display = resolveAdsHeaderActivityPill(activity, auth);
  if (!display?.visible) return null;

  const clickable = typeof onOpenAdsIngestion === "function";
  const baseRowClass =
    "mt-2 flex w-full items-center gap-2 rounded-lg border border-gray-100 bg-white/60 px-2.5 py-1.5 text-left text-xs shadow-sm dark:border-gray-700 dark:bg-gray-900/60";
  const interactiveClass = clickable
    ? "transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:hover:bg-gray-800"
    : "";

  const content = (
    <>
      <StatusDot tone={display.tone} ariaLabel={display.label} />
      <span className="tabular-nums font-medium text-gray-800 dark:text-gray-100">
        {display.showGoal ? (
          <>
            {display.count}
            <span className="font-normal text-gray-500 dark:text-gray-400">/{display.goal}</span>
          </>
        ) : (
          display.count
        )}
      </span>
      <span className="text-[10px] font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">
        ads today
      </span>
      <span className="ml-auto truncate text-[11px] text-gray-600 dark:text-gray-300">
        {display.label}
      </span>
    </>
  );

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Ad alerts
      </div>
      {clickable ? (
        <button
          type="button"
          onClick={onOpenAdsIngestion}
          aria-label={display.ariaLabel + " Open Ads Assistant ingestion."}
          className={`${baseRowClass} ${interactiveClass}`}
        >
          {content}
        </button>
      ) : (
        <div className={baseRowClass} role="status" aria-label={display.ariaLabel}>
          {content}
        </div>
      )}
    </div>
  );
}
